import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LottieIcon } from './LottieIcon';
import { CloseIcon } from './icons';
import { useUiStore } from '../../store/ui';
import { useResendStore } from '../../store/resend';
import { txLottie, txStatusLabel, type Tx } from './TransactionRow';
import { shortenAddress } from '../../lib/walletNames';

// Ticket-style notch cut-outs on the left/right edges, at the divider line.
const NOTCH_Y = 287;
const NOTCH_R = 16;
const NOTCH_MASK =
  `radial-gradient(circle ${NOTCH_R}px at 0px ${NOTCH_Y}px, #0000 ${NOTCH_R}px, #000 ${NOTCH_R}px),` +
  `radial-gradient(circle ${NOTCH_R}px at 100% ${NOTCH_Y}px, #0000 ${NOTCH_R}px, #000 ${NOTCH_R}px)`;

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-[7px]">
      <span className="shrink-0 text-[14px] text-muted">{label}</span>
      <span className="min-w-0 text-right text-[14px] font-medium text-ink">{value}</span>
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  tx: Tx;
};

/** Centered transaction-detail dialog (not a bottom sheet). */
export function TransactionDetailSheet({ open, onClose, tx }: Props) {
  const { t } = useTranslation();
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const requestResend = useResendStore((s) => s.request);

  // A failed outgoing Tron send can be re-opened with the same recipient and
  // amount. The new attempt receives a fresh live energy estimate server-side.
  const canResend =
    tx.status === 'failed' &&
    tx.direction === 'out' &&
    tx.chain === 'trx' &&
    tx.failureReason === 'insufficient_energy' &&
    !!tx.counterparty &&
    !!tx.tokenAmount;
  const onResend = () => {
    if (!canResend || !tx.chain || !tx.token) return;
    onClose();
    requestResend({ chain: tx.chain, token: tx.token, to: tx.counterparty!, amount: tx.tokenAmount! });
  };

  useEffect(() => {
    if (!open) return;
    openSheet();
    return () => closeSheet();
  }, [open, openSheet, closeSheet]);

  const incoming = tx.direction === 'in';
  const isCard = tx.kind === 'card';
  const isSwap = tx.kind === 'swap' || tx.type === 'swap';
  const isCardPurchase = isCard && !!tx.merchant;
  const amountStr = tx.tokenAmount ?? '—';
  const cardAsset = tx.asset || 'USDT';
  const cardIsUsd = cardAsset === 'USDT' || cardAsset === 'USD';
  const cardPaymentAmount = tx.localAmount ?? (tx.paymentAmount ? `${tx.paymentAmount} ${cardAsset}` : '—');
  // Prefer the USD debit Banana billed — same figure history uses for FX purchases.
  const cardDebitLabel = tx.debitAmount
    ? `${tx.debitAmount} USDT`
    : cardIsUsd
      ? `${amountStr} USDT`
      : `${amountStr} ${cardAsset}`;
  const hasBankFee = Number(tx.bankFeeUsd) > 0;
  const hasForexFee = Number(tx.forexFeeUsd) > 0;
  const statusValue = isSwap
    ? txStatusLabel(tx, t)
    : tx.status === 'failed'
      ? t('txDetail.statusFailed')
      : tx.status === 'pending' || tx.status === 'submitted'
        ? t('txDetail.statusPending')
        : t('txDetail.statusValue');

  // Link the hash to the right block explorer (Bitcoin / Tron / Ethereum).
  const explorerUrl = tx.txHash
    ? tx.chain === 'btc'
      ? `https://mempool.space/tx/${tx.txHash}`
      : tx.chain === 'trx' || tx.network === 'TRC-20'
        ? `https://tronscan.org/#/transaction/${tx.txHash}`
        : `https://etherscan.io/tx/${tx.txHash}`
    : null;
  const openExplorer = () => {
    if (!explorerUrl) return;
    const wa = window.Telegram?.WebApp;
    if (wa?.openLink) wa.openLink(explorerUrl);
    else window.open(explorerUrl, '_blank', 'noopener');
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      style={{ pointerEvents: open ? 'auto' : 'none' }}
    >
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/30 transition-opacity duration-500 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Centered card (with ticket notch cut-outs at the divider) */}
      <div
        className={`relative flex max-h-[84vh] w-full max-w-[400px] flex-col overflow-y-auto rounded-[28px] bg-white px-5 pb-6 pt-5 shadow-[0px_20px_60px_rgba(0,0,0,0.18)] transition-all duration-500 ${
          open ? 'scale-100 opacity-100' : 'pointer-events-none scale-95 opacity-0'
        }`}
        style={{
          maskImage: NOTCH_MASK,
          WebkitMaskImage: NOTCH_MASK,
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-medium text-ink">{t('txDetail.title')}</h3>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06] text-ink"
            aria-label="Close"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Amount block */}
        <div className="mt-4 flex flex-col items-center gap-2">
          <div className="flex h-[100px] w-[100px] items-center justify-center overflow-hidden rounded-full bg-[#f2f3f4]">
            <LottieIcon animationData={txLottie(tx)} fill loop autoplay />
          </div>
          <span className="mt-1 text-[14px] text-muted">
            {isSwap ? t('swap.txLabel') : incoming ? t('txDetail.received') : t('txDetail.sent')}
          </span>
          <span className="max-w-full break-words text-center text-[28px] font-semibold leading-tight tabular-nums text-ink">
            {amountStr}
            <span className="font-medium text-muted"> {tx.asset}</span>
          </span>
          {isCard ? (
            tx.merchant ? (
              <span className="max-w-full break-words text-center text-[14px] text-muted">
                {tx.merchant}
              </span>
            ) : null
          ) : isSwap ? (
            tx.swapPair ? (
              <span className="max-w-full px-2 text-center text-[14px] text-muted">{tx.swapPair}</span>
            ) : null
          ) : (
            <span className="text-[14px] text-muted">≈ ${amountStr}</span>
          )}
        </div>

        {/* Divider */}
        <div className="my-5 border-t border-dashed border-black/15" />

        {/* Details */}
        <div>
          {isCard ? (
            <>
              <Row label={t('txDetail.status')} value={statusValue} />
              {tx.merchant ? <Row label={t('txDetail.merchant')} value={tx.merchant} /> : null}
              <Row
                label={isCardPurchase ? t('txDetail.debitAmount') : t('txDetail.amount')}
                value={cardDebitLabel}
              />
              {isCardPurchase && cardIsUsd ? (
                <Row label={t('txDetail.paymentAmount')} value={cardPaymentAmount} />
              ) : null}
              {hasBankFee ? (
                <Row label={t('txDetail.bankFee')} value={`${tx.bankFeeUsd} USD`} />
              ) : null}
              {hasForexFee ? (
                <Row label={t('txDetail.forexFee')} value={`${tx.forexFeeUsd} USD`} />
              ) : null}
              <Row label={t('txDetail.date')} value={tx.fullDate ?? tx.date} />
            </>
          ) : isSwap ? (
            <>
              <Row label={t('txDetail.type')} value={t('swap.txLabel')} />
              <Row label={t('txDetail.status')} value={statusValue} />
              {tx.swapPair ? <Row label={t('swap.pair')} value={tx.swapPair} /> : null}
              {tx.swapFromAmount ? <Row label={t('swap.youGive')} value={tx.swapFromAmount} /> : null}
              <Row
                label={t('swap.youGet')}
                value={
                  tx.swapToAmount && tx.swapToToken
                    ? `${tx.swapToAmount} ${tx.swapToToken}`
                    : '—'
                }
              />
              <Row label={t('txDetail.date')} value={tx.fullDate ?? tx.date} />
              {tx.partnerOrderId ? (
                <Row label={t('swap.orderId')} value={shortenAddress(tx.partnerOrderId)} />
              ) : null}
              {explorerUrl ? (
                <div className="flex items-center justify-between py-[7px]">
                  <span className="text-[14px] text-muted">{t('txDetail.hash')}</span>
                  <button
                    onClick={openExplorer}
                    className="flex items-center gap-1 text-[14px] font-medium text-[#377dff] active:opacity-70"
                  >
                    {shortenAddress(tx.txHash!)}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M14 4h6v6M20 4l-9 9M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <Row label={t('txDetail.type')} value={t('txDetail.typeValue')} />
              <Row label={t('txDetail.status')} value={statusValue} />
              <Row label={t('txDetail.amount')} value={`${amountStr} ${tx.asset}`} />
              {/* Native amounts are coin units, not ≈USD — skip the $ row
                  rather than presenting 0.001 ETH as "$0.001". */}
              {tx.token !== 'ETH' && tx.token !== 'BTC' && tx.token !== 'TRX' && (
                <Row label={t('txDetail.amountUsd')} value={`$${amountStr}`} />
              )}
              <Row label={t('txDetail.blockchain')} value={tx.network} />
              <Row
                label={t('txDetail.fee')}
                // Unknown fee (provider sent no billing data) shows "—", never a
                // fabricated $0.00; an explicit zero fee still shows $0.00.
                value={tx.feeUsd != null ? `$${tx.feeUsd}${tx.feePct ? ` (${tx.feePct}%)` : ''}` : '—'}
              />
              <Row label={t('txDetail.date')} value={tx.fullDate ?? tx.date} />
              <Row
                label={
                  incoming
                    ? t('txDetail.sender')
                    : tx.asset === 'INCPT'
                      ? t('incpt.send.recipient')
                      : t('txDetail.recipient')
                }
                value={tx.counterparty ? shortenAddress(tx.counterparty) : '—'}
              />
              {explorerUrl ? (
                <div className="flex items-center justify-between py-[7px]">
                  <span className="text-[14px] text-muted">{t('txDetail.hash')}</span>
                  <button
                    onClick={openExplorer}
                    className="flex items-center gap-1 text-[14px] font-medium text-[#377dff] active:opacity-70"
                  >
                    {shortenAddress(tx.txHash!)}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M14 4h6v6M20 4l-9 9M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <Row label={t('txDetail.hash')} value="—" />
              )}
            </>
          )}
        </div>

        {canResend && (
          <button
            onClick={onResend}
            className="mt-5 flex h-12 items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
          >
            {t('txDetail.resend')}
          </button>
        )}
      </div>
    </div>
  );
}
